import React, { useCallback, useState } from 'react';
import isEqual from 'lodash/isEqual';
import Modal from '@creditflowresearch/uilib/Modal';
import Button from '@creditflowresearch/uilib/Button';
import { Link } from '@framework/router/Next/Link';
import URL from '@/creditflowresearch/Router/urlConstants';
import FinalForm, { Field, type TFinalFormRenderProp } from '@framework/ui/Organisms/FinalForm';
import SubscribeButton from '@/creditflowresearch/ui/components/creditFlow/articles/common/SubscribeButton';
import {
  getSubscriptionsArticleParams,
} from '@/creditflowresearch/application/ArticlesAndStats/ArticlesSubscriptions/helpers';
import ArticleSubscriptionsContextProvider
  from '@/creditflowresearch/application/ArticlesAndStats/ArticlesSubscriptions/ArticlesSubscriptionsContext';
import UserSubscriptionsContextProvider, {
  useUserSubscriptionsContext,
} from '@/creditflowresearch/application/Subscriptions/UserSubscriptionsContext/UserSubscriptionsContext';
import { getInitialThemesState, getSubscribeThemeIds } from './helpers';
import { useArticlesSubscriptionsContext } from '@/creditflowresearch/application/ArticlesAndStats/ArticlesSubscriptions';
import { useIsGuest } from '@/creditflowresearch/application/User/access';
import LoginOrSignUpPopover from '@/creditflowresearch/ui/common/components/LoginOrSignUpPopover';
import { useAdapterConfig } from './useAdapterConfig';
import FilterGroupList from './FilterGroupList';
import { useSubscriptionAdapter } from './useSubscriptionAdapter';

import styles from './PageSubscriptionButton.module.scss';

const PageSubscriptionButton = () => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const {
    onSubscribe,
    themesIds,
    themes,
    groupedThemes,
    isThemesFetching,
    currentProduct,
  } = useArticlesSubscriptionsContext();
  const {
    userSubscriptions,
    isFetching: isSubscriptionsFetching,
  } = useUserSubscriptionsContext();
  const isGuest = useIsGuest();
  const { articleParams: { themeIds: themesSubscriptionIds } } = getSubscriptionsArticleParams(userSubscriptions);
  const closeModal = () => setIsVisible(false);

  const onOpenForm = () => setIsVisible(true);

  const initialState = getInitialThemesState(themesIds, themesSubscriptionIds);

  const adapter = useAdapterConfig();
  const { regionIds, modalTitle, shouldFilterByProduct, filterGroups } = useSubscriptionAdapter(adapter);

  const onSubmitForm = async (values: ReturnType<typeof getInitialThemesState>) => {
    const themesToSubscribe = getSubscribeThemeIds(values, {
      themes,
      themesSubscriptionIds,
      currentRegionIds: regionIds,
      currentProductId: shouldFilterByProduct ? currentProduct.id : undefined,
    });
    await onSubscribe(themesToSubscribe);
    closeModal();
  };

  const renderForm = useCallback<TFinalFormRenderProp<ReturnType<typeof getInitialThemesState>>>(({
    form,
    values,
    initialValues,
    submitting,
  }) => (
    <>
      <div className={ styles.modalContent }>
        <FilterGroupList
          form={ form }
          fieldComponent={ Field }
          formValues={ values }
          groups={ filterGroups }
        />
      </div>
      <div className={ styles.modalFooter }>
        <Button
          onClick={ form.submit }
          disabled={ isEqual(values, initialValues) || submitting }
          variant="solid"
          color="primary"
          dataTest="manageSubscriptionsModalConfirmButton"
        >
          Confirm
        </Button>
        <Link
          dataTest="manageSubscriptionsModalLinkToSubscriptions"
          to={ URL.SUBSCRIPTIONS }
        >My Subscriptions
        </Link>
      </div>
    </>
  ), [groupedThemes, themesIds, modalTitle]);

  return (
    <>
      <LoginOrSignUpPopover isRestricted={ isGuest } placement="bottom" isBlockLevelTrigger>
        <SubscribeButton
          tooltip="Manage Email Alerts"
          onClick={ onOpenForm }
          isDisabled={ isGuest }
          isTooltipDisabled={ isGuest }
          isFetching={ isThemesFetching || isSubscriptionsFetching }
        />
      </LoginOrSignUpPopover>
      <Modal
        isVisible={ isVisible }
        className={ styles.modal }
        title={ (
          <>
            <h3>
              { modalTitle }
            </h3>
            <div className={ styles.modalSubtitle }>Manage your email subscriptions</div>
          </>
        ) }
        onOverlayClicked={ closeModal }
        handleCloseClick={ closeModal }
        dataTest="manageSubscriptionsModal"
      >
        <FinalForm
          dataTest="themesSubscriptionsForm"
          onSubmit={ onSubmitForm }
          render={ renderForm }
          initialValues={ initialState }
        />
      </Modal>
    </>
  );
};

export default () => (
  <UserSubscriptionsContextProvider>
    <ArticleSubscriptionsContextProvider>
      <PageSubscriptionButton />
    </ArticleSubscriptionsContextProvider>
  </UserSubscriptionsContextProvider>
);
